/** @jsxImportSource @emotion/react */
import { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { AutoSizeMode, CellRange, CellType } from '@grapecity/wijmo.grid';
import { Workbook } from '@grapecity/wijmo.xlsx';
import { Button, Tab, Tabs } from '@mui/material';
import {
  SearchBox,
  SearchBoxRow,
  InputBox,
  SearchRows,
  SearchCols,
  SearchButtonWrap,
} from 'components/layouts/SearchBox';
import { MultiComboBox } from 'components/selects/ComboBox';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { Code } from 'models/common/CommonCode';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import { getCommonCodeNames, getCommonCodeNamesCondition } from 'apis/admin/CommonCode';
import { tabs } from 'components/layouts/Tab';
import CustomGrid, { CustomMergeManager } from 'components/grids/CustomGrid';
import { ControlBtnGroup, SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import { CommonUtil } from 'utils/CommonUtil';
import CustomChip from 'components/selects/CustomChip';
import { findEquipmentSpec, findEquipmentSpecHistory } from 'apis/md/MdEquipmentSpec';
import { getExcelFileName } from 'utils/ExcelUtil';

/**
 * @desc 투자비관리 > 기준정보 > 설비사양 조회
 *    -> 설비사양 > 설비사양관리 > 설비사양 조회
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.05.26   jshwang12       신규 생성
 * 2026.08.25   jshwang12       상위 헤더가 다른 경우 하위 헤더 병합 방지 (설비군 다른 경우 중간헤더 병합안함)
 */
const MdEqpSpecInqPage = () => {
  const gridRef = useRef<any>();
  const { t } = useTranslation();
  const [currentTabIndex, setCurrentTabIndex] = useState<number>(0);
  const [rowData, setRowData] = useState<any>();
  const [histRowData, setHistRowData] = useState<any[]>([]);
  const [code, setCode] = useState<any>();
  const initCondition = {
    bildPlntCds: [],
    prdnLnNm: '',
    eqclIds: [],
    procChrtSpecNm: '',
  };
  const [condition, setCondition] = useState<any>({ ...initCondition });
  const [selectedEqclIds, setSelectedEqclIds] = useState<string[]>(['ALL']);
  const [selectedEqclIdsByTab, setSelectedEqclIdsByTab] = useState<Record<string, string[]>>({});

  const tabMenus = useMemo(() => {
    const def = {
      HIST: String(t('com.label.변경이력', '변경이력')),
    };
    if ((code?.mdPrdnLnTpCd || []).length < 1) {
      return Object.keys(def).reduce(
        (acc, cur) => [
          ...acc,
          {
            id: cur,
            name: def[cur],
          },
        ],
        [] as any[]
      );
    }
    return [
      ...(code?.mdPrdnLnTpCd || []).reduce(
        (acc, cur) => [
          ...acc,
          {
            id: cur.cmnCd,
            name: def[cur.cmnCd] || cur.cmnCdNm,
          },
        ],
        []
      ),
      {
        id: 'HIST',
        name: def['HIST'],
      },
    ];
  }, [code?.mdPrdnLnTpCd]);

  useEffect(() => {
    getCommonCodes();
    handleSearch();
  }, []);

  useEffect(() => {
    const prdnLnTpCd = tabMenus?.[currentTabIndex]?.id;
    const currentTabSelectedIds = selectedEqclIdsByTab[prdnLnTpCd || ''];

    if (currentTabSelectedIds && currentTabSelectedIds.length > 0) {
      setSelectedEqclIds([...currentTabSelectedIds]);
    } else {
      setSelectedEqclIds(['ALL']);
    }
  }, [currentTabIndex, selectedEqclIdsByTab, code]);

  const filterRowData = useMemo(() => {
    const prdnLnTpCd = tabMenus?.[currentTabIndex]?.id;
    if (rowData && prdnLnTpCd && prdnLnTpCd !== 'HIST') {
      if (Object.keys(rowData || {}).filter((key) => key === prdnLnTpCd)) {
        return rowData[prdnLnTpCd] || [];
      }
    }
    return [];
  }, [tabMenus, currentTabIndex, rowData]);

  /**
   * 라인유형별 설비군 목록
   * cf. [{cmnCd: 설비군ID, cmnCdNm: 설비군명}, ...]
   */
  const filteredEqclIds = useMemo(() => {
    const prdnLnTpCd = tabMenus?.[currentTabIndex]?.id;
    return [
      { cmnCd: 'ALL', cmnCdNm: String(t('com.label.전체', '전체')) },
      ...Array.from(
        new Map<string, Code>(
          (code?.[`mdEqclSpecMappingBy${prdnLnTpCd || ''}`] || []).map((item) => [
            item.optValCtn1,
            { cmnCd: item.optValCtn1, cmnCdNm: item.optValCtn6 },
          ])
        ).values()
      ),
    ];
  }, [code, currentTabIndex]);

  const getCommonCodes = async () => {
    const bildPlntCd: Code[] = await getCommonCodeNames('BILD_PLNT_CD'); // 건설플랜트코드
    const mdPrdnLnTpCd: Code[] = await getCommonCodeNames('MD_PRDN_LN_TP_CD'); // 생산라인유형코드
    const mdEqclId: Code[] = await getCommonCodeNamesCondition({
      cmnGrCd: 'MD_EQCL_ID',
      optValCtn1: 'Y', // 설비사양대상 (설비기준)
    });
    const mappingCds = {} as any; // 탭(라인유형)별 매핑코드 (cf. {라인유형코드: [매핑코드목록], ..})
    const eqclIds = {} as any; // 탭(라인유형)별 선택된 설비군 초기값
    if ((mdPrdnLnTpCd || []).length) {
      await Promise.all(
        mdPrdnLnTpCd.map(async (o) => {
          if (o.cmnCd) {
            const mappingCd = await getCommonCodeNamesCondition({
              cmnGrCd: 'MD_EQCL_SPEC_MAPPING',
              optValCtn5: o.cmnCd, // 라인유형(전극/셀/모듈)
              optValCtn9: 'Y', // 템플릿업로드(화면표시) 항목여부
            });
            mappingCds[`mdEqclSpecMappingBy${o.cmnCd}`] = mappingCd;
            // 매핑된 설비군 모두 선택된 상태로 초기값 설정
            eqclIds[o.cmnCd] = ['ALL'];
          }
        })
      );
    }

    setSelectedEqclIdsByTab(eqclIds);
    setCode({
      bildPlntCd: bildPlntCd,
      mdPrdnLnTpCd: mdPrdnLnTpCd,
      mdEqclId: mdEqclId,
      ...(mappingCds || {}),
    });
  };

  const handleSearch = () => {
    findEquipmentSpec(condition).then((spec) => {
      setRowData(spec);
    });
    findEquipmentSpecHistory(condition).then((hist) => {
      setHistRowData(hist);
    });
  };

  const handleOnChange = (name: string, value: any) => {
    setCondition((prev) => ({ ...prev, [name]: value }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const convertPrdnLnTpLayout = (mappingCds) => {
    const groupMid = (mappingCds || []).reduce((acc, cur) => {
      const topCtgrNm = cur.optValCtn6; // 그리드 상단(1번째)
      const midCtgrNm = cur.optValCtn7; // 그리드 중간(2번째)
      if (!acc[topCtgrNm]) {
        acc[topCtgrNm] = {};
      }
      if (!acc[topCtgrNm][midCtgrNm]) {
        acc[topCtgrNm][midCtgrNm] = [];
      }
      acc[topCtgrNm][midCtgrNm].push({
        binding: `'${cur.cmnCd}'`,
        header: cur.optValCtn8, // 그리드 하단(3번째)
        minWidth: 80,
        width: 100,
        align: 'center',
      });
      return acc;
    }, {});

    const columns = [] as any[];
    Object.keys(groupMid).forEach((topCtgrNm) => {
      const topCol = {
        header: topCtgrNm,
        columns: [] as any[],
      };

      Object.keys(groupMid[topCtgrNm]).forEach((midCtgrNm) => {
        topCol.columns.push({
          header: midCtgrNm,
          columns: groupMid[topCtgrNm][midCtgrNm],
        });
      });
      columns.push(topCol);
    });
    return columns;
  };

  const mapLayoutDefinition = useMemo(() => {
    return (code?.mdPrdnLnTpCd || []).reduce((acc, cur) => {
      return {
        ...acc,
        [cur.cmnCd]: [
          {
            binding: 'BILD_PLNT_CD_NM',
            header: String(t('com.label.플랜트', '플랜트')),
            minWidth: 90,
            width: '*',
            align: 'center',
          },
          {
            binding: 'BLDO_NM',
            header: String(t('com.label.동 명', '동 명')),
            minWidth: 80,
            width: '*',
            align: 'center',
          },
          {
            binding: 'PRDN_LN_NM',
            header: String(t('md.label.생산라인', '생산라인')),
            width: '*',
            minWidth: 100,
            align: 'center',
          },
          ...convertPrdnLnTpLayout(
            (code?.[`mdEqclSpecMappingBy${cur.cmnCd || ''}`] || []).filter(
              (o) =>
                (selectedEqclIdsByTab[cur.cmnCd || ''] || []).includes(o.optValCtn1) ||
                (selectedEqclIdsByTab[cur.cmnCd || ''] || []).includes('ALL')
            )
          ),
        ],
      };
    }, {});
  }, [code, selectedEqclIdsByTab]);

  const histLayoutDefinition = [
    {
      binding: 'bildPlntCdNm',
      header: String(t('com.label.플랜트', '플랜트')),
      minWidth: 90,
      width: 90,
      align: 'center',
    },
    {
      binding: 'bldoNm',
      header: String(t('com.label.동 명', '동 명')),
      minWidth: 80,
      width: 80,
      align: 'center',
    },
    {
      binding: 'prdnLnNm',
      header: String(t('md.label.생산라인', '생산라인')),
      width: 100,
      minWidth: 100,
      align: 'center',
    },
    {
      binding: 'eqclNm',
      header: String(t('md.label.설비군', '설비군')),
      width: 100,
      minWidth: 100,
      align: 'center',
    },
    {
      binding: 'eltrTpCdNm',
      header: String(t('md.label.전극구분', '전극구분')),
      width: 100,
      minWidth: 100,
      align: 'center',
    },
    {
      binding: 'procChrtSpecNm',
      header: String(t('md.label.사양항목명', '사양항목명')),
      width: '*',
      minWidth: 100,
      align: 'center',
    },
    {
      binding: 'specValCtn',
      header: String(t('md.label.현재사양값', '현재사양값')),
      width: '*',
      minWidth: 100,
      align: 'center',
    },
    {
      binding: 'bfSpecValCtn',
      header: String(t('md.label.이전사양값', '이전사양값')),
      width: '*',
      minWidth: 100,
      align: 'center',
    },
    {
      binding: 'dataUpdUserNm',
      header: String(t('com.label.수정자', '수정자')),
      minWidth: 70,
      width: 100,
      align: 'center',
    },
    {
      binding: 'dataUpdDtm',
      header: String(t('com.label.수정일자', '수정일자')),
      minWidth: 70,
      width: 120,
      align: 'center',
    },
  ];

  const handleDownloadExcel = () => {
    const workbook = new Workbook();
    for (const tab of tabMenus || []) {
      // if (tab.id === tabMenus[currentTabIndex].id) {
      const ws = CommonUtil.createWijmoSheet(
        tab.name,
        tab.id === 'HIST' ? histLayoutDefinition : mapLayoutDefinition?.[tab.id],
        tab.id === 'HIST' ? histRowData : rowData[tab.id]
      );
      workbook.sheets.push(ws);
      // }
    }
    workbook.saveAsync(getExcelFileName(t('md.label.설비사양', '설비사양')));
  };

  useEffect(() => {
    requestAnimationFrame(() => {
      const prdnLnTpCd = tabMenus?.[currentTabIndex]?.id;
      if (gridRef.current?.hostElement && prdnLnTpCd !== 'HIST') {
        gridRef.current.autoSizeColumns();
      }
    });
  }, [mapLayoutDefinition]);

  const onInitialized = (grid) => {
    gridRef.current = grid;
    grid.autoSizeMode = AutoSizeMode.Headers;
    // 상위 헤더가 다른 경우 하위 헤더 병합 방지
    grid.mergeManager = new HeaderGroupMergeManager();
    grid.updatedLayout.addHandler(function s() {
      grid.updatedLayout.removeHandler(s);

      if (grid.columns.length > 3) {
        grid.autoSizeColumns();
      }
    });
  };

  // 설비군 칩이 가로로 넘칠 때 아래로 줄바꿈: 기본 레이아웃은 높이 28px 고정
  const eqclChipLayoutCss = {
    height: 'auto',
    alignItems: 'flex-start',
  };
  const eqclChipGroupCss = {
    maxWidth: '100%',
    '& .MuiStack-root': {
      flexWrap: 'wrap' as const,
      columnGap: '4px',
      rowGap: '4px',
      height: 'auto',
    },
    // 줄바꿈 시 줄 첫 칩이 밀리지 않도록 margin 대신 gap 사용
    '& .MuiStack-root .MuiChip-root + .MuiChip-root': { marginLeft: 0 },
  };

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows className={tabMenus?.[currentTabIndex]?.id === 'HIST' ? 'FourCol' : ''}>
              <SearchCols>
                <label>{String(t('com.label.플랜트', '플랜트'))}</label>
                <MultiComboBox
                  id="bildPlntCd"
                  options={code?.bildPlntCd || []}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={condition?.bildPlntCds || []}
                  msgError={String(t('com.msg.플랜트를 선택해 주세요.', '플랜트를 선택해 주세요.'))}
                  onChange={(value) => handleOnChange('bildPlntCds', value)}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('md.label.생산라인', '생산라인'))}</label>
                <CustomInputWithSearch
                  name="prdnLnNm"
                  value={condition.prdnLnNm}
                  placeholder={String(
                    t('md.label.생산라인을 입력해 주세요.', '생산라인을 입력해 주세요.')
                  )}
                  onChange={(e) => handleOnChange('prdnLnNm', e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              {tabMenus?.[currentTabIndex]?.id === 'HIST' && (
                <>
                  <SearchCols>
                    <label>{String(t('md.label.설비군', '설비군'))}</label>
                    <MultiComboBox
                      id="eqclId"
                      options={code?.mdEqclId || []}
                      placeholder={String(t('com.label.전체', '전체'))}
                      defaultValue={condition?.eqclIds || []}
                      onChange={(value) => handleOnChange('eqclIds', value)}
                    />
                  </SearchCols>
                  <SearchCols>
                    <label>{String(t('md.label.사양항목', '사양항목'))}</label>
                    <CustomInputWithSearch
                      name="procChrtSpecNm"
                      value={condition.procChrtSpecNm}
                      placeholder={String(
                        t('md.label.사양항목을 입력해 주세요.', '사양항목을 입력해 주세요.')
                      )}
                      onChange={(e) => handleOnChange('procChrtSpecNm', e.target.value)}
                      onKeyDown={handleKeyDown}
                    />
                  </SearchCols>
                </>
              )}
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <SearchBoxButtons
              defaultCondition={initCondition}
              setReset={setCondition}
              onSearch={() => handleSearch()}
            />
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>
      <div css={tabs.wrap} style={{ marginTop: '8px', marginBottom: '8px' }}>
        <Tabs
          value={currentTabIndex}
          onChange={(e, newValue) => setCurrentTabIndex(newValue)}
          css={tabs.tab}
          style={{ marginBottom: '8px' }}
        >
          {tabMenus.map((o, idx) => (
            <Tab
              key={o.id}
              label={o.name}
              id={`md-eqp-spec-inq-tab-${idx}`}
              aria-controls={`md-eqp-spec-inq-tabpanel-${idx}`}
            />
          ))}
        </Tabs>
        <div css={tabs.btn}>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '화면 다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => handleSearch()}
            disableRipple
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </div>
      </div>
      {tabMenus.map((o, idx) => (
        <TabPanel key={o.id} value={currentTabIndex} index={idx}>
          {tabMenus?.[currentTabIndex]?.id === 'HIST' ? (
            <>
              <SubTitleLayout>
                <SubTitleGroup>
                  <h3>{t('md.label.변경이력 목록', '변경이력 목록')}</h3>
                  <span className="total">
                    {t('com.label.총', '총')}
                    <span>{(histRowData || []).length.toLocaleString()}</span>
                    {t('com.label.건', '건')}
                  </span>
                </SubTitleGroup>
              </SubTitleLayout>
              <CustomGrid
                id={`mdEquipmentSpecInqGrid_HIST`}
                layoutDefinition={histLayoutDefinition}
                rowData={histRowData}
                isSelector={false}
                isReadOnly={true}
                height={556}
              />
            </>
          ) : (
            <>
              <SubTitleLayout css={eqclChipLayoutCss}>
                <ControlBtnGroup css={eqclChipGroupCss}>
                  <CustomChip
                    options={filteredEqclIds || []}
                    defaultValue={selectedEqclIds || []}
                    singleSelect={false}
                    onChange={(value) => {
                      let values = (value || []) as string[];
                      const prdnLnTpCd = tabMenus?.[currentTabIndex]?.id;
                      if (values.includes('ALL')) {
                        if (selectedEqclIds.includes('ALL') && values.length > 1) {
                          values = values.filter((v) => v !== 'ALL');
                        } else {
                          values = ['ALL'];
                        }
                      }
                      if (values.length < 1) {
                        values = [...selectedEqclIds];
                      }
                      setSelectedEqclIds(values);
                      setSelectedEqclIdsByTab((prev) => ({
                        ...prev,
                        [prdnLnTpCd || '']: values,
                      }));
                    }}
                  />
                </ControlBtnGroup>
              </SubTitleLayout>
              <SubTitleLayout style={{ marginTop: '8px' }}>
                <SubTitleGroup>
                  <h3>{t('md.label.설비사양 목록', '설비사양 목록')}</h3>
                  <span className="total">
                    {t('com.label.총', '총')}
                    <span>{(filterRowData || []).length.toLocaleString()}</span>
                    {t('com.label.건', '건')}
                  </span>
                </SubTitleGroup>
              </SubTitleLayout>
              <CustomGrid
                id={`mdEquipmentSpecInqGrid_${tabMenus[currentTabIndex].id}`}
                layoutDefinition={mapLayoutDefinition[tabMenus[currentTabIndex].id]}
                rowData={filterRowData}
                isSelector={false}
                isReadOnly={true}
                height={448}
                frozenColumns={3}
                initialized={onInitialized}
              />
            </>
          )}
        </TabPanel>
      ))}
    </>
  );
};

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function TabPanel(props: { [x: string]: any; children: any; value: any; index: any }) {
  const { children, value, index } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`md-eqp-spec-inq-tabpanel-${index}`}>
      {value === index && <div>{children}</div>}
    </div>
  );
}

/**
 * 3단 헤더 병합 관리자
 * cf. 헤더 텍스트가 같더라도 상위(groupTop) 헤더가 다른 경우 좌우 병합하지 않음
 * cf. 상/중/하 헤더가 모두 같은 단일 항목(플랜트, 동명, 생산라인 등)만 상하 병합하고,
 *     일부만 같은 경우(상=Mixer/중=Mixer/하=최대속도)는 상하 병합하지 않음
 */
export class HeaderGroupMergeManager extends CustomMergeManager {
  getMergedRange(panel, r, c, clip = true) {
    if (panel.cellType !== CellType.ColumnHeader) {
      return super.getMergedRange(panel, r, c, clip);
    }

    const rng = new CellRange(r, c);
    const rowCount = panel.rows.length;

    // 해당 컬럼의 헤더 행이 모두 같은 문구인지 (= 단일 항목)
    const isSingleHeader = (col) => {
      for (let i = 1; i < rowCount; i++) {
        if (panel.getCellData(i, col, true) != panel.getCellData(0, col, true)) {
          return false;
        }
      }
      return true;
    };
    if (isSingleHeader(c)) {
      rng.row = 0;
      rng.row2 = rowCount - 1;
    }

    // 두 컬럼이 0행부터 lastRow까지 모두 같은 문구인지 (상위 헤더 + 병합 대상 행 포함)
    const isSameUpTo = (col1, col2, lastRow) => {
      for (let i = 0; i <= lastRow; i++) {
        if (panel.getCellData(i, col1, true) != panel.getCellData(i, col2, true)) {
          return false;
        }
      }
      return true;
    };
    const canMerge = (col1, col2) =>
      panel.columns[col1].cssClass !== 'noMerge' &&
      panel.columns[col2].cssClass !== 'noMerge' &&
      isSameUpTo(col1, col2, rng.row2);

    for (let i = c; i < panel.columns.length - 1; i++) {
      if (!canMerge(i, i + 1)) break;
      rng.col2 = i + 1;
    }
    for (let i = c; i > 0; i--) {
      if (!canMerge(i, i - 1)) break;
      rng.col = i - 1;
    }
    return rng;
  }
}

export default MdEqpSpecInqPage;
